import {
  CheckSquareOutlined,
  CloseOutlined,
  DeleteFilled,
  EditFilled,
  LinkOutlined,
  PlusCircleOutlined
} from "@ant-design/icons";
import { Badge, Button, Checkbox, Col, DatePicker, List, Row, Select, Space, Spin, Typography } from "antd";
import moment from "moment-timezone";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  createPatientsTaskAPI,
  deletePatientsTaskAPI,
  editPatientsTaskAPI,
  getFinancialNavigationPotentialSavingPhis,
  getPatientsTasksAPI
} from "../../api/api";
import { ReactComponent as TasksFilled } from "../../assets/svg/tasks.svg";
import { GRAY_700, PRIMARY_600, WHITE } from "../../constant/colors";
import { ALL, DONE, THIS_MONTH, THIS_WEEK, UNASSIGNED } from "../../constant/filters";
import { useFetch } from "../../hooks/fetch";
import {
  selectAssigneesAndUserGroups,
  selectBadgeCounts,
  selectFormPopups,
  selectPatientDetails,
  selectUser
} from "../../store/selector";
import { DATE_FORMAT, DATE_FORMAT_1, dateFormatter, setDateRange } from "../../utils/date";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { CircleIconButton } from "../UI/CircleIconButton";
import { TasksEdit } from "./TasksEdit";

import { Tooltip } from "../UI/Tooltip";

import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import { fontWeights, sizes } from "../../constant/styles";
import { useGetSetting } from "../../hooks/getSetting";
import ACTIONS from "../../store/action";
import { formatAssignee } from "../../utils/formaters";
import { getPopupPlacement } from "../../utils/patient";
import { optionsSearch } from "../../utils/search";
import { Popover } from "../UI/Antd/Popover/Popover";
import ExportTasksData from "./ExportTasksData";
import "./Tasks.css";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const STATUS_NOT_DONE = "";
const taskPopoverSize = {
  Height: {
    patientView: 243,
    patientTaskEditView: 290
  }
};

const PATIENTS_ON_PAGE = 10;
const STATUS_CHECKED = "checked";
const FILTERS_TASKS = "filterTasks";

const MenuButton = styled(CircleIconButton)`
  padding-top: 4px;
`;

const StyledSelect = styled(Select)`
  .anticon > svg {
    color: rgb(82, 80, 85);
  }

  .ant-select-selector {
    padding: ${(props) => !props.withoutPadding && "0px 20px 0px 0px !important"};
  }
`;

export const getPopoverHeight = (isPatientView, isEditMode = false) =>
  isPatientView ? (isEditMode ? taskPopoverSize.Height.patientTaskEditView : taskPopoverSize.Height.patientView) : null;

const TaskPotentialSavingLink = ({
  taskJourneyPatientId,
  taskJourneyId,
  potentialSavingName,
  linkedPotentialSaving,
  currentPatientId,
  currentJourneyId
}) => {
  const { t } = useTranslation();
  const psName = potentialSavingName || t("patientsTasks.view_program");

  if (!linkedPotentialSaving) return null;

  const patientId = taskJourneyPatientId || currentPatientId;
  const journeyId = taskJourneyId || currentJourneyId;

  if (!patientId || !journeyId) {
    return (
      <span>
        <LinkOutlined style={{ paddingRight: 8 }} />
        {psName}
      </span>
    );
  }

  return (
    <Link to={`/patient/${patientId}/journey/${journeyId}/optimizations/${linkedPotentialSaving}`}>
      <LinkOutlined style={{ paddingRight: 8 }} />
      {psName}
    </Link>
  );
};

export const Tasks = ({ isPatientView, ...props }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { patientId, journeyId } = useParams();

  const [prefix] = useGetSetting(["id_prefix"]);

  const saveFiltersToLocalStorage = (filters) => {
    localStorage.setItem(FILTERS_TASKS, JSON.stringify(filters));
  };

  const getFiltersFromLocalStorage = () => {
    const filters = localStorage.getItem(FILTERS_TASKS);
    return filters ? JSON.parse(filters) : {};
  };

  const filters = getFiltersFromLocalStorage();
  const { start, end } = filters?.dueDate || {};

  const user = useSelector(selectUser);
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const patientDetails = useSelector(selectPatientDetails);
  const formPopups = useSelector(selectFormPopups);
  const badgeCounts = useSelector(selectBadgeCounts);
  const { patientTasks } = badgeCounts;

  const [page, setPage] = useState(1);
  const [potentialSavingMap, setPotentialSavingMap] = useState({});
  const [selectedAssignees, setSelectedAssignees] = useState(filters.assignees);
  const [selectedStatus, setSelectedStatus] = useState(filters.status || STATUS_NOT_DONE);
  const [selectedPeriod, setSelectedPeriod] = useState(filters.period || ALL);
  const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const [isAssigneeOpen, setIsAssigneeOpen] = useState(false);
  const [isSelectPeriodOpen, setIsSelectPeriodOpen] = useState(false);
  const [placement, setPlacement] = useState("bottomLeft");
  const [displayTasks, setDisplayTasks] = useState([]);

  const [editTask, editPatientsTaskResult, editPatientsTaskIsFetching, editPatientsTaskError] =
    useFetch(editPatientsTaskAPI);
  const [getTasksAPI, getTasksResult, getTasksIsFetching, getTasksError] = useFetch(getPatientsTasksAPI);
  const [deleteTaskAPI, deleteTaskResult, deleteTaskIsFetching, deleteTaskError] = useFetch(deletePatientsTaskAPI);
  const [createTaskAPI, createTaskResult, createTaskIsFetching, createTaskError] = useFetch(createPatientsTaskAPI);
  const [fetchPotentialSaving, potentialSavingResult, potentialSavingIsFetching, potentialSavingError] = useFetch(
    getFinancialNavigationPotentialSavingPhis
  );

  const formatDate = (date, format) => (date ? moment(date, DATE_FORMAT).format(format) : "");

  const formattedStart = formatDate(start, DATE_FORMAT_1);
  const formattedEnd = formatDate(end, DATE_FORMAT_1);
  const periodValue = end ? `${formattedStart} - ${formattedEnd}` : selectedPeriod;

  const setFormPopupsAct = (visible, contentKey) => dispatch(ACTIONS.setFormPopups(visible, contentKey));
  const { visible, contentKey } = formPopups;
  const formPopupsContentKey = patientId ? formPopupsContentKeys.PATIENT_TASKS : formPopupsContentKeys.TASKS;
  const isTaskPopoverOpen = visible && contentKey === formPopupsContentKey ? true : false;

  useEffect(() => {
    const updatedFilters = getFiltersFromLocalStorage();
    setSelectedAssignees(updatedFilters.assignees);
    setSelectedStatus(updatedFilters.status || STATUS_NOT_DONE);
    setSelectedPeriod(updatedFilters.period || ALL);
  }, [visible]);

  useEffect(() => {
    if (getTasksResult?.data?.todos) {
      const tasks = getTasksResult.data.todos;
      setDisplayTasks(tasks);
      dispatch(ACTIONS.setBadgeCounts("tasks", getTasksResult.data.allTasksTotal || getTasksResult.data.total));
      if (isPatientView) {
        dispatch(ACTIONS.setBadgeCounts("patientTasks", { total: getTasksResult?.data.total, patientId: patientId }));
        setDisplayTasks(tasks.filter((task) => task.patient === parseInt(patientId)));
      }
    }
  }, [getTasksResult, dispatch, patientId, isPatientView]);

  useEffect(() => {
    if (assignees.length === 0 || !user) return;

    const currentAssigneeUser = assignees?.find((assignee) => assignee.id?.toString() === user?.id?.toString())?.id;
    const initialAssignee = filters.assignees || currentAssigneeUser || ALL;

    setSelectedAssignees(initialAssignee);
  }, [assignees, user]);

  useEffect(() => {
    const potentialSavings = potentialSavingResult?.data?.["financial-navigation-potential-saving-phi"];
    if (potentialSavings) {
      setPotentialSavingMap({
        ...potentialSavings.reduce((acc, { id, ...result }) => {
          return {
            ...acc,
            [id]: result
          };
        }, {}),
        ...potentialSavingMap
      });
    }
  }, [potentialSavingResult]);

  useEffect(() => {
    saveFiltersToLocalStorage({
      ...filters,
      assignees: selectedAssignees,
      status: selectedStatus,
      period: selectedPeriod
    });
    setIsEditMode(false);

    const { start, end } = filters?.dueDate || {};
    getTasks(start, end);
  }, [editPatientsTaskResult, createTaskResult, selectedAssignees, selectedStatus, selectedPeriod, deleteTaskResult]);

  useEffect(() => {
    if (!visible || patientId || contentKey !== formPopupsContentKeys.TASKS) return;

    const handleClickOutside = (event) => {
      const popoverElement = document.querySelector("#tasks_popover");

      if (popoverElement && !popoverElement.contains(event.target)) {
        setFormPopupsAct(false, formPopupsContentKeys.TASKS);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [visible, contentKey, patientId]);

  useEffect(async () => {
    const fetchLinkedPotentialSaving = displayTasks
      .filter((p) => p.linkedPotentialSaving)
      .map((p) => p.linkedPotentialSaving);
    const fetchingList = [];

    if (fetchLinkedPotentialSaving.length) {
      fetchingList.push(fetchPotentialSaving(fetchLinkedPotentialSaving));
    }
    await Promise.all(fetchingList);
  }, [page, displayTasks.length]);

  useEffect(() => {
    if (createTaskResult?.data?.todo || editPatientsTaskResult?.data?.todo || visible) {
      const { start, end } = filters?.dueDate || {};
      getTasks(start, end);
    }
  }, [createTaskResult, editPatientsTaskResult, visible]);

  const hasError = editPatientsTaskError || getTasksError || deleteTaskError || createTaskError || potentialSavingError;

  if (hasError) {
    ErrorMessage(hasError);
  }

  const isFetching =
    editPatientsTaskIsFetching ||
    getTasksIsFetching ||
    deleteTaskIsFetching ||
    createTaskIsFetching ||
    potentialSavingIsFetching;

  const getTasks = async (start = null, end = null, nextPage) => {
    const filterData = {
      assignee: selectedAssignees === UNASSIGNED ? "" : selectedAssignees,
      status: selectedStatus,
      dueDate: start ? { start, end } : selectedPeriod
    };
    const addPatientData = isPatientView ? false : true;
    const patientIdParam = isPatientView ? patientId : null;
    await getTasksAPI(filterData, addPatientData, patientIdParam, PATIENTS_ON_PAGE, nextPage || page);
  };

  const selectPeriodHandler = (value) => {
    if (value === filters.period) getTasks();

    setSelectedPeriod(value);
    setIsSelectPeriodOpen(false);
    saveFiltersToLocalStorage({ ...filters, dueDate: { start: "", end: "" } });
  };

  const setRangePicker = (values) => {
    setIsRangePickerOpen(false);
    const formattedDates = setDateRange(moment(values[0]), moment(values[1]));
    const dateFrom = formattedDates[0];
    const dateTo = formattedDates[1];
    getTasks(dateFrom, dateTo);

    saveFiltersToLocalStorage({ ...filters, dueDate: { start: dateFrom, end: dateTo } });
    setIsSelectPeriodOpen(false);
  };

  const changeStatusHandler = (task) => {
    if (task.status === STATUS_CHECKED) {
      task.status = null;
    } else if (task.status === null) {
      task.status = STATUS_CHECKED;
    }
    editTask(task);
  };

  const editingTaskHandler = useCallback((task) => {
    setIsEditMode(true);
    setEditTaskData(task);
  }, []);

  const onLoadMore = () => {
    setPage(page + 1);
    const nextPage = page + 1;
    const { start, end } = filters?.dueDate || {};
    getTasks(start, end, nextPage);
  };

  const getAssigneeLabel = () => {
    switch (selectedAssignees) {
      case ALL:
        return t("patientsTasks.all_assignees");
      case UNASSIGNED:
        return t("patientsTasks.unassigned");
      default:
        return selectedAssignees;
    }
  };

  const loadMore = !isFetching ? (
    <>
      {page < Math.ceil(getTasksResult?.data.total / PATIENTS_ON_PAGE) && (
        <div style={{ textAlign: "center", marginTop: 12, height: 32, lineHeight: "32px" }}>
          <Button onClick={onLoadMore}>{t("patientsTasks.load_more")}</Button>
        </div>
      )}
    </>
  ) : null;

  const popoverTitle = (
    <div>
      <Text id={`${patientId ? "patient_name_" : ""}tasks_popover_title`}>
        {patientId ? `${t("patientsTasks.tasks_for")} ${patientDetails?.name || ""}` : t("patientsTasks.title")}
      </Text>
      <CloseOutlined id="close_tasks_popover" onClick={() => setFormPopupsAct(false, formPopupsContentKey)} />
    </div>
  );

  const renderItem = (task) => {
    const { text, dueDate, patient, mrn } = task;
    const taskAssignee = assignees?.find((assignee) => assignee.id?.toString() === task.assignee?.toString());
    const patientFullName = task.patientFullName || "Patient";

    return (
      <List.Item id={`task-${task.id}`}>
        <List.Item.Meta
          avatar={
            <Checkbox
              style={{ paddingTop: task.journey ? 4 : 0 }}
              checked={task.status === STATUS_CHECKED}
              onClick={() => changeStatusHandler(task)}
              id="task_status_checkbox"
            />
          }
          title={
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {task.journey && (
                  <Link
                    style={{ padding: 0 }}
                    type="link"
                    id="patient_name"
                    to={`/patient/${patient}/journey/${task.journey.id}`}
                  >
                    {patientFullName}
                  </Link>
                )}
                {!isPatientView && (patient || mrn) && (
                  <Text id="patient_id_and_mrn">
                    {"("}
                    {prefix ? prefix.toUpperCase() : "TM"}
                    {`-${patient}`}
                    {mrn && t("patientsTasks.mrn", { mrn })}
                    {")"}
                  </Text>
                )}
              </div>
              <div>
                <Text id="task-description">{text}</Text>
              </div>
              <TaskPotentialSavingLink
                taskJourneyPatientId={task?.journey?.patient}
                taskJourneyId={task?.journey?.id}
                linkedPotentialSaving={task.linkedPotentialSaving}
                potentialSavingName={task.assistanceProgram || potentialSavingMap[task.linkedPotentialSaving]?.name}
                currentPatientId={patientId}
                currentJourneyId={journeyId}
              />
            </>
          }
          description={
            <Row>
              {dueDate && (
                <Col span={5}>
                  <Text style={{ color: moment().isBefore(moment(dueDate)) ? "black" : "red" }}>
                    {dateFormatter(dueDate)}
                  </Text>
                </Col>
              )}
              <Col span={dueDate ? 13 : 18} id="task-assignee">
                {taskAssignee && `${formatAssignee(taskAssignee)}`}
              </Col>
              <Col span={6}>
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                  <EditFilled id="edit_task_button" onClick={() => editingTaskHandler(task)} />
                  <DeleteFilled
                    id="delete_task_button"
                    onClick={() => {
                      if (!isPatientView && parseInt(patientTasks.patientId) === task.patient) {
                        dispatch(
                          ACTIONS.setBadgeCounts("patientTasks", { ...patientTasks, total: patientTasks.total - 1 })
                        );
                      }
                      deleteTaskAPI(task);
                    }}
                  />
                </Space>
              </Col>
            </Row>
          }
        />
      </List.Item>
    );
  };

  const tasksList = (
    <Space direction={"vertical"}>
      <Space style={{ display: "flex", justifyContent: "space-between", gap: 0 }}>
        <Space size={0}>
          <div id="tasks_assignee_filter">
            <StyledSelect
              withoutPadding={true}
              value={getAssigneeLabel()}
              dropdownMatchSelectWidth={false}
              onSelect={setSelectedAssignees}
              style={{ fontWeight: 700, marginLeft: 5 }}
              bordered={false}
              showSearch={true}
              open={isAssigneeOpen}
              onDropdownVisibleChange={(visible) => setIsAssigneeOpen(visible)}
              filterOption={optionsSearch}
              dropdownRender={(menu) => (
                <>
                  <div style={{ display: "flex", flexWrap: "nowrap", padding: 8, gap: 6 }}>
                    <Button
                      type={selectedAssignees === ALL ? "primary" : "default"}
                      ghost={selectedAssignees === ALL ? true : false}
                      onClick={() => (setSelectedAssignees(ALL), setIsAssigneeOpen(false))}
                    >
                      {t("patientsTasks.all_assignees")}
                    </Button>
                    <Button
                      type={selectedAssignees === UNASSIGNED ? "primary" : "default"}
                      ghost={selectedAssignees === UNASSIGNED ? true : false}
                      onClick={() => {
                        setSelectedAssignees(UNASSIGNED);
                        setIsAssigneeOpen(false);
                      }}
                    >
                      {t("patientsTasks.unassigned")}
                    </Button>
                  </div>
                  {menu}
                </>
              )}
              filterSort={(optionA, optionB) =>
                optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
              }
            >
              {assignees?.map((assignee) => (
                <Option key={assignee.id} value={assignee.id}>
                  {formatAssignee(assignee)}
                </Option>
              ))}
            </StyledSelect>
          </div>

          <div id="tasks_status_filter">
            <StyledSelect
              value={selectedStatus}
              dropdownMatchSelectWidth={false}
              onSelect={setSelectedStatus}
              style={{ fontWeight: 700 }}
              bordered={false}
            >
              <Option value={ALL}>{t("patientsTasks.show_all")}</Option>
              <Option value={STATUS_NOT_DONE}>{t("patientsTasks.not_done")}</Option>
              <Option value={DONE}>{t("patientsTasks.done")}</Option>
            </StyledSelect>
          </div>

          <div id="tasks_period_filter">
            <StyledSelect
              value={periodValue}
              dropdownMatchSelectWidth={false}
              onSelect={selectPeriodHandler}
              style={{ fontWeight: 700 }}
              bordered={false}
              open={isSelectPeriodOpen}
              onClick={() => {
                if (!isSelectPeriodOpen) {
                  setIsSelectPeriodOpen(true);
                }
              }}
              onBlur={() => {
                if (!isRangePickerOpen) {
                  setIsSelectPeriodOpen(false);
                }
              }}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <RangePicker
                    open={isRangePickerOpen}
                    value={[start && moment(start, DATE_FORMAT), end && moment(end, DATE_FORMAT)]}
                    bordered={false}
                    suffixIcon={null}
                    clearIcon={null}
                    separator={null}
                    allowEmpty={[false, false]}
                    onVisibleChange={() => setIsRangePickerOpen(false)}
                    onChange={setRangePicker}
                    onOpenChange={(open) => setIsRangePickerOpen(open)}
                  />
                </div>
              )}
            >
              <Option value={ALL}>{t("all_times")}</Option>
              <Option value={THIS_WEEK}>{t("this_week")}</Option>
              <Option value={THIS_MONTH}>{t("this_month")}</Option>
            </StyledSelect>
          </div>
        </Space>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 16px 4px 0px"
          }}
        >
          {!isPatientView && <ExportTasksData filterData={filters} setFormPopupsAct={setFormPopupsAct} />}

          <Tooltip
            title={
              <Text style={{ color: GRAY_700, fontSize: sizes.small, fontWeight: fontWeights.semibold }}>
                {t("patientsTasks.add_task")}
              </Text>
            }
            color={WHITE}
          >
            <Button
              id="add_task_button"
              style={{ padding: 0 }}
              onClick={() => {
                setIsEditMode(true);
                setEditTaskData(null);
              }}
              type="text"
            >
              <PlusCircleOutlined style={{ color: PRIMARY_600 }} />
            </Button>
          </Tooltip>
        </div>
      </Space>
      <List
        style={{
          padding: "0px 16px",
          width: "100%",
          height: getPopoverHeight(isPatientView),
          overflowY: "auto"
        }}
        loadMore={loadMore}
        dataSource={displayTasks}
        renderItem={renderItem}
        loading={isFetching}
      />
    </Space>
  );

  const handleVisibleChange = (visible) => {
    !visible && setIsSelectPeriodOpen(false);
    if (!isPatientView) {
      visible && setFormPopupsAct(visible, formPopupsContentKey);
      return;
    }

    visible && getPopupPlacement(setPlacement, "patient_tasks");

    setFormPopupsAct(visible, formPopupsContentKey);
  };

  return (
    <Popover
      trigger="click"
      id={isPatientView ? "patient_tasks_popover" : "tasks_popover"}
      placement={placement}
      style={{ padding: 0 }}
      title={popoverTitle}
      open={isTaskPopoverOpen}
      onVisibleChange={(visible) => handleVisibleChange(visible)}
      content={
        <div style={{ display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
          {!isEditMode && tasksList}
          {isEditMode && (
            <TasksEdit
              isFetching={isFetching}
              editTaskData={editTaskData}
              setIsEditMode={setIsEditMode}
              editTask={editTask}
              createTask={createTaskAPI}
              isPatientView={isPatientView}
            />
          )}
        </div>
      }
      {...props}
    >
      {!patientId ? (
        <Badge offset={[-5, 5]} size="small" overflowCount={999} count={badgeCounts.tasks}>
          <Tooltip title={t("patientsTasks.tasks")} placement="bottom">
            <MenuButton
              id="main_view_tasks_button"
              icon={<TasksFilled />}
              onClick={(e) => {
                e.stopPropagation();
                setFormPopupsAct(!isTaskPopoverOpen, formPopupsContentKey);
              }}
            />
          </Tooltip>
        </Badge>
      ) : (
        <div
          id="patient_tasks"
          style={{ display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setFormPopupsAct(!visible, formPopupsContentKey);
          }}
        >
          <CheckSquareOutlined style={{ fontSize: 14, paddingRight: 3 }} />
          <Text style={{ fontSize: 14, marginRight: 3 }}>{t("patientRSActions.tasks")}</Text>
          {getTasksIsFetching && isPatientView ? (
            <Spin size="small" />
          ) : patientTasks.total ? (
            ` (${patientTasks.total})`
          ) : (
            ""
          )}
        </div>
      )}
    </Popover>
  );
};

export default memo(Tasks);
